'use client';

/**
 * Client-side document text extraction utilities for StudioAI
 * Extracts text from PDF, XLSX, CSV, TXT, DOCX, and DOC files directly in the browser
 *
 * Libraries used:
 * - unpdf: PDF text extraction (lightweight, browser-compatible)
 * - exceljs: XLSX/XLS parsing
 * - papaparse: CSV parsing
 * - mammoth: DOCX/DOC text extraction
 */

import type { ExtractedDocument, ExtractionProgress } from '@/@studio-ai/types';
import { displayOfNumber } from '@/lib/crosstab/xlsx-number-format';
import { tracking } from '@/lib/features/tracking';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';

// ============================================================================
// PDF EXTRACTION (using unpdf - lightweight PDF extraction library)
// ============================================================================

/**
 * Extract text from a PDF file using unpdf
 * unpdf is a modern, lightweight PDF text extraction library
 */
async function extractPdfText(
    arrayBuffer: ArrayBuffer,
    onProgress?: (progress: ExtractionProgress) => void,
): Promise<string> {
    onProgress?.({ stage: 'parsing', progress: 20, message: 'Loading PDF library...' });

    // Dynamic import to avoid SSR issues
    const { extractText } = await import('unpdf');

    onProgress?.({ stage: 'extracting', progress: 40, message: 'Extracting text from PDF...' });

    // Convert ArrayBuffer to Uint8Array for unpdf
    const data = new Uint8Array(arrayBuffer);

    // Extract text from all pages (returns array of strings, one per page)
    const result = await extractText(data, { mergePages: false });

    onProgress?.({ stage: 'extracting', progress: 80, message: 'Formatting text...' });

    // Format with page markers
    const textParts = result.text
        .map((pageText, index) => {
            const trimmed = pageText.trim();
            return trimmed ? `--- Page ${index + 1} ---\n${trimmed}` : null;
        })
        .filter(Boolean);

    return textParts.join('\n\n');
}

// ============================================================================
// XLSX EXTRACTION (using ExcelJS)
// ============================================================================

/**
 * Extract text from an XLSX/XLS file
 */
async function extractXlsxText(
    arrayBuffer: ArrayBuffer,
    onProgress?: (progress: ExtractionProgress) => void,
): Promise<string> {
    onProgress?.({ stage: 'parsing', progress: 20, message: 'Loading spreadsheet...' });

    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const textParts: string[] = [];
    const totalSheets = workbook.worksheets.length;
    let currentSheet = 0;

    for (const worksheet of workbook.worksheets) {
        currentSheet++;
        onProgress?.({
            stage: 'extracting',
            progress: 20 + Math.round((currentSheet / totalSheets) * 70),
            message: `Processing sheet ${currentSheet} of ${totalSheets}...`,
        });

        textParts.push(`\n--- Sheet: ${worksheet.name} ---\n`);

        const rows: string[][] = [];
        const columnWidths: number[] = [];

        // Collect all data and calculate column widths
        worksheet.eachRow((row) => {
            const rowData: string[] = [];
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const cellValue = formatCellValue(cell);
                rowData[colNumber - 1] = cellValue;

                // Track max width per column (capped at 50)
                const width = Math.min(cellValue.length, 50);
                if (!columnWidths[colNumber - 1] || width > columnWidths[colNumber - 1]) {
                    columnWidths[colNumber - 1] = width;
                }
            });
            rows.push(rowData);
        });

        // Format as readable table
        rows.forEach((rowData, rowIndex) => {
            const formattedRow = rowData
                .map((cell, colIndex) => (cell || '').padEnd(columnWidths[colIndex] || 0))
                .join(' | ');

            textParts.push(formattedRow);

            // Add separator after header row
            if (rowIndex === 0) {
                const separator = columnWidths.map((w) => '-'.repeat(w)).join('-+-');
                textParts.push(separator);
            }
        });
    }

    return textParts.join('\n');
}

/**
 * Format an ExcelJS cell value to string
 */
function formatCellValue(cell: ExcelJS.Cell): string {
    const value = cell.value;

    if (value === null || value === undefined) {
        return '';
    }

    // Handle rich text
    if (typeof value === 'object' && 'richText' in value && Array.isArray(value.richText)) {
        return value.richText.map((rt) => rt.text).join('');
    }

    // Handle formula results
    if (typeof value === 'object' && 'result' in value) {
        return String(value.result ?? '');
    }

    // Handle hyperlinks
    if (typeof value === 'object' && 'hyperlink' in value && 'text' in value) {
        return String(value.text ?? '');
    }

    // Handle dates
    if (value instanceof Date) {
        return value.toISOString().split('T')[0];
    }

    // Handle errors
    if (typeof value === 'object' && 'error' in value) {
        return '#ERROR';
    }

    // Handle other objects
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    // A number reads as the text its format shows ("72% AB"), as crosstab exports wrote it before M4A2A-570.
    if (typeof value === 'number') return displayOfNumber(value, cell.numFmt);

    return String(value);
}

// ============================================================================
// CSV EXTRACTION (using PapaParse)
// ============================================================================

/**
 * Extract and format text from a CSV file using PapaParse
 */
function extractCsvText(text: string, onProgress?: (progress: ExtractionProgress) => void): string {
    onProgress?.({ stage: 'parsing', progress: 30, message: 'Parsing CSV...' });

    const result = Papa.parse<string[]>(text, {
        skipEmptyLines: true,
    });

    if (result.errors.length > 0) {
        console.warn('CSV parsing warnings:', result.errors);
    }

    const rows = result.data;

    if (rows.length === 0) {
        return text;
    }

    onProgress?.({ stage: 'extracting', progress: 60, message: 'Formatting data...' });

    // Calculate column widths (capped at 50)
    const columnWidths: number[] = [];
    for (const row of rows) {
        for (let i = 0; i < row.length; i++) {
            const cellLength = Math.min((row[i] || '').length, 50);
            if (!columnWidths[i] || cellLength > columnWidths[i]) {
                columnWidths[i] = cellLength;
            }
        }
    }

    // Format as readable table
    const formattedRows = rows.map((row, rowIndex) => {
        const formattedRow = row
            .map((cell, colIndex) => (cell || '').padEnd(columnWidths[colIndex] || 0))
            .join(' | ');

        // Add separator after header row
        if (rowIndex === 0) {
            const separator = columnWidths.map((w) => '-'.repeat(w)).join('-+-');
            return `${formattedRow}\n${separator}`;
        }

        return formattedRow;
    });

    return formattedRows.join('\n');
}

// ============================================================================
// TXT EXTRACTION (plain text)
// ============================================================================

/**
 * Extract text from a plain text file
 */
function extractTxtText(text: string, onProgress?: (progress: ExtractionProgress) => void): string {
    onProgress?.({ stage: 'extracting', progress: 50, message: 'Reading text file...' });
    return text.trim();
}

// ============================================================================
// DOCX EXTRACTION (using Mammoth)
// ============================================================================

/**
 * Extract text from a DOCX file using Mammoth
 * Mammoth extracts the text content while preserving basic structure
 */
async function extractDocxText(
    arrayBuffer: ArrayBuffer,
    onProgress?: (progress: ExtractionProgress) => void,
): Promise<string> {
    onProgress?.({ stage: 'parsing', progress: 20, message: 'Loading Word document...' });

    // Dynamic import to avoid SSR issues
    const mammoth = await import('mammoth');

    onProgress?.({
        stage: 'extracting',
        progress: 50,
        message: 'Extracting text from document...',
    });

    // Extract raw text from the document
    const result = await mammoth.extractRawText({ arrayBuffer });

    if (result.messages && result.messages.length > 0) {
        // Log any warnings but don't fail
        tracking.errorWithSource(
            new Error('DOCX extraction warnings'),
            'document_extraction_warning',
            { messages: result.messages },
        );
    }

    onProgress?.({ stage: 'extracting', progress: 80, message: 'Formatting text...' });

    return result.value.trim();
}

/**
 * Extract text from a DOC file using Mammoth
 * Note: Mammoth has limited support for .doc files (legacy binary format)
 * It works for some .doc files but may fail for others
 */
async function extractDocText(
    arrayBuffer: ArrayBuffer,
    onProgress?: (progress: ExtractionProgress) => void,
): Promise<string> {
    onProgress?.({ stage: 'parsing', progress: 20, message: 'Loading Word document...' });

    // Dynamic import to avoid SSR issues
    const mammoth = await import('mammoth');

    onProgress?.({
        stage: 'extracting',
        progress: 50,
        message: 'Extracting text from document...',
    });

    try {
        // Try to extract text using mammoth (works for some .doc files)
        const result = await mammoth.extractRawText({ arrayBuffer });

        if (result.messages && result.messages.length > 0) {
            console.warn('DOC extraction warnings:', result.messages);
        }

        const text = result.value.trim();

        // If we got meaningful text, return it
        if (text && text.length > 0) {
            onProgress?.({ stage: 'extracting', progress: 80, message: 'Formatting text...' });
            return text;
        }

        // If no text was extracted, provide a helpful message about .doc limitations
        throw new Error(
            'No text could be extracted from this .doc file. The legacy .doc format has limited browser support. ' +
                'Please save the document as .docx (Word 2007+) format and try again.',
        );
    } catch (error) {
        // Log the original error for debugging
        tracking.errorWithSource(error as Error, 'document_extraction_error');

        // Re-throw if it's already our custom error message
        if (error instanceof Error && error.message.includes('.doc')) {
            throw error;
        }

        // For other errors, provide context while preserving the original message
        const originalMessage = error instanceof Error ? error.message : 'Unknown error';
        throw new Error(
            `Failed to process .doc file: ${originalMessage}. ` +
                'If the issue persists, try saving the document as .docx format.',
        );
    }
}

// ============================================================================
// MAIN EXTRACTION FUNCTION
// ============================================================================

/**
 * Extract text from a document file
 * Supports PDF, XLSX, XLS, CSV, TXT, DOCX, and DOC files - runs entirely client-side
 */
export async function extractDocumentText(
    file: File,
    onProgress?: (progress: ExtractionProgress) => void,
): Promise<ExtractedDocument> {
    const fileName = file.name;
    const contentType = file.type;
    const size = file.size;
    const extension = fileName.split('.').pop()?.toLowerCase();

    onProgress?.({ stage: 'reading', progress: 10, message: 'Reading file...' });

    let extractedText: string;
    let extractionMethod: ExtractedDocument['extractionMethod'];

    try {
        switch (extension) {
            case 'pdf': {
                const arrayBuffer = await file.arrayBuffer();
                extractedText = await extractPdfText(arrayBuffer, onProgress);
                extractionMethod = 'pdf';
                break;
            }

            case 'xlsx':
            case 'xls': {
                const arrayBuffer = await file.arrayBuffer();
                extractedText = await extractXlsxText(arrayBuffer, onProgress);
                extractionMethod = 'xlsx';
                break;
            }

            case 'csv': {
                const text = await file.text();
                extractedText = extractCsvText(text, onProgress);
                extractionMethod = 'csv';
                break;
            }

            case 'txt': {
                const text = await file.text();
                extractedText = extractTxtText(text, onProgress);
                extractionMethod = 'text';
                break;
            }

            case 'docx': {
                const arrayBuffer = await file.arrayBuffer();
                extractedText = await extractDocxText(arrayBuffer, onProgress);
                extractionMethod = 'docx';
                break;
            }

            case 'doc': {
                const arrayBuffer = await file.arrayBuffer();
                extractedText = await extractDocText(arrayBuffer, onProgress);
                extractionMethod = 'doc';
                break;
            }

            default: {
                // Fallback: read as plain text
                onProgress?.({ stage: 'extracting', progress: 50, message: 'Reading text...' });
                extractedText = await file.text();
                extractionMethod = 'text';
                break;
            }
        }

        // Truncate extremely long documents
        const MAX_LENGTH = 100000;
        if (extractedText.length > MAX_LENGTH) {
            extractedText =
                extractedText.substring(0, MAX_LENGTH) +
                '\n\n[... Document truncated due to size ...]';
        }

        onProgress?.({ stage: 'complete', progress: 100, message: 'Extraction complete' });

        return {
            fileName,
            contentType,
            size,
            extractedText,
            extractionMethod,
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Document extraction failed:', error);
        throw new Error(`Failed to extract text from ${fileName}: ${errorMessage}`);
    }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Maps a file extension to the appropriate extraction method
 * Shared utility to avoid duplication between extraction and content rendering
 */
export function getExtractionMethodFromExtension(
    ext: string | undefined,
): ExtractedDocument['extractionMethod'] {
    switch (ext?.toLowerCase()) {
        case 'pdf':
            return 'pdf';
        case 'xlsx':
        case 'xls':
            return 'xlsx';
        case 'csv':
            return 'csv';
        case 'docx':
            return 'docx';
        case 'doc':
            return 'doc';
        case 'txt':
        default:
            return 'text';
    }
}

/**
 * Format extracted document text for inclusion in an AI prompt
 */
export function formatDocumentForPrompt(doc: ExtractedDocument, userMessage: string): string {
    return `${userMessage}\n\n[Document: ${doc.fileName}]\n---\n${doc.extractedText}\n---`;
}

/**
 * Parse embedded document format from a message text
 * Detects the format: ${userMessage}\n\n[Document: ${fileName}]\n---\n${extractedText}\n---
 *
 * @param text - The text that may contain an embedded document
 * @returns Parsed result with user message and optional document info
 */
export function parseEmbeddedDocument(text: string): {
    userMessage: string;
    document: {
        fileName: string;
        extractedText: string;
    } | null;
} {
    // Match the embedded document format
    // Pattern: captures user message, then [Document: filename], then content between --- markers
    const documentPattern = /^([\s\S]*?)\n\n\[Document:\s*(.+)\]\n---\n([\s\S]*)\n---$/;

    const match = text.match(documentPattern);

    if (match) {
        const [, userMessage, fileName, extractedText] = match;
        return {
            userMessage: userMessage.trim(),
            document: {
                fileName: fileName.trim(),
                extractedText: extractedText.trim(),
            },
        };
    }

    // No embedded document found
    return {
        userMessage: text,
        document: null,
    };
}
